// src/pages/Browser/components/BrowserHeader.js
import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaLock, FaWallet, FaShieldAlt } from 'react-icons/fa';
import { WalletContext } from '../../../contexts/WalletContext';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { getChainLogo } from '../../Wallet/chainLogos';
import { getChainList } from '../../../chains';
import './BrowserHeader.css';

const BrowserHeader = ({
  url,
  title,
  isLoading,
  onUrlChange,
  onNavigate,
  isShowingHome
}) => {
  const { t } = useTranslation();
  const { currentWallet, userWallets = [], setCurrentWallet } = useContext(WalletContext);
  const { currentChain, availableNetworks = {}, switchNetwork } = useContext(NetworkContext);
  
  // Use useMemo to compute networks list and avoid re-computation on every render
  const networksList = useMemo(() => {
    if (!availableNetworks || Object.keys(availableNetworks).length === 0) {
      try {
        const chainList = getChainList();
        const networksFromChains = {};
        
        chainList.forEach(chain => {
          networksFromChains[chain.key] = {
            name: chain.name,
            chainId: chain.chainId,
            key: chain.key
          };
        });
        
        return networksFromChains;
      } catch (error) {
        console.error('Error getting chain list:', error);
        return {};
      }
    } else {
      return availableNetworks;
    }
  }, [availableNetworks]);
  
  const [inputUrl, setInputUrl] = useState(url);
  const [isEditing, setIsEditing] = useState(false);
  const [showNetworkMenu, setShowNetworkMenu] = useState(false);
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  
  const inputRef = useRef(null);
  const networkRef = useRef(null);
  const walletRef = useRef(null);

  // Update internal state when external URL changes
  useEffect(() => {
    setInputUrl(url);
  }, [url]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (networkRef.current && !networkRef.current.contains(event.target)) {
        setShowNetworkMenu(false);
      }
      if (walletRef.current && !walletRef.current.contains(event.target)) {
        setShowWalletMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setInputUrl(e.target.value);
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    // Select all text for easy editing
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    // Revert to actual URL if user didn't submit
    setInputUrl(url);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputUrl.trim()) {
      onNavigate(inputUrl.trim());
      setIsEditing(false);
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const handleNetworkSwitch = (networkKey) => {
    if (switchNetwork) {
      switchNetwork(networkKey);
      setTimeout(() => {
        setShowNetworkMenu(false);
      }, 100);
    }
  };

  const handleWalletSwitch = (wallet) => {
    if (setCurrentWallet) {
      setCurrentWallet(wallet);
    }
    setShowWalletMenu(false);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      inputRef.current.blur();
    }
  };

  // Get display URL (showing protocol for editing, hiding it for display)
  const displayUrl = isEditing 
    ? inputUrl 
    : (url && url !== 'about:blank' ? url.replace(/^https?:\/\//, '') : '');

  // Extract hostname for favicon
  const hostname = url && url !== 'about:blank' ? new URL(url).hostname : '';
  
  return (
    <>
      <div className="browser-header">
        <div className="browser-url-container">
          {!isShowingHome && (
            <form className="browser-url-bar" onSubmit={handleSubmit}>
              {isLoading ? (
                <div className="browser-loading-indicator"></div>
              ) : (
                <>
                  <FaLock className="secure-icon" />
                  <img 
                    src={`https://${hostname}/favicon.ico`} 
                    alt="" 
                    className="site-icon"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </>
              )}
              <input
                ref={inputRef}
                type="text"
                value={displayUrl}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                placeholder={t('browser.enterUrl')}
              />
            </form>
          )}
          
          {isShowingHome && (
            <form className="browser-url-bar" onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={t('browser.enterUrl')}
                onKeyDown={handleKeyDown}
              />
            </form>
          )}
        </div>
        
        <div className="browser-actions">
          {/* Network Selector - Icon Only */}
          <div className="browser-dropdown-container" ref={networkRef}>
            <button 
              className="browser-dropdown-button network-button icon-only"
              onClick={() => setShowNetworkMenu(!showNetworkMenu)}
              aria-label={t('browser.selectNetwork')}
            >
              <div className="browser-network-icon">
                <img 
                  src={currentChain ? getChainLogo(currentChain.key) : '/assets/wallet-logo.png'} 
                  alt={currentChain?.name || 'Network'}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/wallet-logo.png';
                  }}
                />
              </div>
            </button>
            
            {showNetworkMenu && (
              <div className="dropdown-menu network-menu">
                {networksList && Object.keys(networksList).length > 0 ? (
                  Object.keys(networksList).map(networkKey => (
                    <button 
                      key={networkKey}
                      className={`menu-item ${currentChain && networkKey === currentChain.key ? 'active' : ''}`}
                      onClick={() => handleNetworkSwitch(networkKey)}
                    >
                      <div className="browser-network-icon">
                        <img 
                          src={getChainLogo(networkKey)} 
                          alt={networksList[networkKey]?.name || networkKey}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/assets/wallet-logo.png';
                          }}
                        />
                      </div>
                      <span>{networksList[networkKey]?.name || networkKey}</span>
                    </button>
                  ))
                ) : (
                  <div className="menu-item">
                    <span>{t('browser.noNetworksAvailable', 'No networks available')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Wallet Selector - Icon Only */}
          <div className="browser-dropdown-container" ref={walletRef}>
            <button 
              className="browser-dropdown-button wallet-button icon-only"
              onClick={() => setShowWalletMenu(!showWalletMenu)}
              aria-label={t('browser.selectWallet')}
            >
              <div className="wallet-icon">
                <FaWallet />
              </div>
            </button>
            
            {showWalletMenu && (
              <div className="dropdown-menu wallet-menu">
                {userWallets && userWallets.length > 0 ? (
                  userWallets.map(wallet => (
                    <button 
                      key={wallet.address}
                      className={`menu-item ${wallet.address === currentWallet?.address ? 'active' : ''}`}
                      onClick={() => handleWalletSwitch(wallet)}
                    >
                      <div className="wallet-icon">
                        <FaWallet />
                      </div>
                      <div className="wallet-info">
                        <div className="wallet-name">
                          {wallet.name || t('wallet.account', { number: wallet.index + 1 })}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="menu-item">
                    <span>{t('browser.noWallets', 'No wallets available')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BrowserHeader;